'use client';

import { c as _c } from "react/compiler-runtime";
import { useMemo, useState } from 'react';
import { cx } from 'class-variance-authority';
import { Button } from '~/components/primitives';
import type { ClassValue } from 'class-variance-authority/types';
import { usePortalCtx } from '~/features/global/portal';
import { backgroundColorVariant, textColorVariant, type BackgroundColorVariant } from '~/utils/variants';
const LEFT_PAGE = 'LEFT';
const RIGHT_PAGE = 'RIGHT';
type NumberedPaginationProps = {
  lastPage: number;
  /** The current nth page */
  current: number;
  previousText: string;
  nextText: string;
  /** @default 2 */
  pageNeighbours?: number;
  onChangePage?: (newPage: number) => void;
  className?: ClassValue;
  backgroundColor?: BackgroundColorVariant;
};
function range(from: number, to: number, step: number = 1) {
  let i = from;
  const rangeArray = [];
  while (i <= to) {
    rangeArray.push(i);
    i += step;
  }
  return rangeArray;
}
export default function NumberedPagination(t0) {
  const $ = _c(67);
  const {
    lastPage,
    current,
    pageNeighbours: t1,
    onChangePage,
    previousText,
    nextText,
    className,
    backgroundColor
  } = t0;
  const pageNeighbours = t1 === undefined ? 2 : t1;
  const portal = usePortalCtx();
  const [currentPage, setCurrentPage] = useState(current);
  if (currentPage > lastPage && lastPage !== 0) {
    setCurrentPage(1);
  }
  let t2;
  if ($[0] !== onChangePage) {
    t2 = number => {
      setCurrentPage(number);
      onChangePage?.(number);
    };
    $[0] = onChangePage;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const handleChangePage = t2;
  const [previousCurrent, setPreviousCurrent] = useState(current);
  if (current !== previousCurrent) {
    setPreviousCurrent(current);
    setCurrentPage(current);
  }
  let t3;
  bb0: {
    const totalNumbers = pageNeighbours * 2;
    const totalBlocks = totalNumbers + 2;
    if (lastPage > totalBlocks) {
      const startPage = Math.max(2, currentPage - pageNeighbours);
      const endPage = Math.min(lastPage - 1, currentPage + pageNeighbours);
      let pages;
      if ($[2] !== endPage || $[3] !== lastPage || $[4] !== startPage || $[5] !== totalNumbers) {
        pages = range(startPage, endPage);
        const hasLeftSpill = startPage > 2;
        const hasRightSpill = lastPage - endPage > 1;
        const spillOffset = totalNumbers - (pages.length + 1);
        bb1: switch (true) {
          case hasLeftSpill && !hasRightSpill:
            {
              const extraPages_0 = range(startPage - spillOffset, startPage - 1);
              pages = [LEFT_PAGE, ...extraPages_0, ...pages];
              break bb1;
            }
          case !hasLeftSpill && hasRightSpill:
            {
              const extraPages = range(endPage + 1, endPage + spillOffset);
              pages = [...pages, ...extraPages, RIGHT_PAGE];
              break bb1;
            }
          case hasLeftSpill && hasRightSpill:
          default:
            {
              pages = [LEFT_PAGE, ...pages, RIGHT_PAGE];
            }
        }
        $[2] = endPage;
        $[3] = lastPage;
        $[4] = startPage;
        $[5] = totalNumbers;
        $[6] = pages;
      } else {
        pages = $[6];
      }
      let t4;
      if ($[7] !== lastPage || $[8] !== pages) {
        t4 = [1, ...pages, lastPage];
        $[7] = lastPage;
        $[8] = pages;
        $[9] = t4;
      } else {
        t4 = $[9];
      }
      t3 = t4;
      break bb0;
    }
    let t4;
    if ($[10] !== lastPage) {
      t4 = range(1, lastPage);
      $[10] = lastPage;
      $[11] = t4;
    } else {
      t4 = $[11];
    }
    t3 = t4;
  }
  const pageNumbers = t3;
  let t4;
  bb2: {
    if (lastPage <= 3) {
      t4 = pageNumbers;
      break bb2;
    }
    let mobilePages;
    if ($[12] !== currentPage || $[13] !== lastPage) {
      mobilePages = [];
      mobilePages.push(1);
      if (currentPage > 2) {
        if (currentPage > 3) {
          mobilePages.push(LEFT_PAGE);
        }
      }
      if (currentPage !== 1 && currentPage !== lastPage) {
        mobilePages.push(currentPage);
      }
      if (currentPage < lastPage - 1) {
        if (currentPage < lastPage - 2) {
          mobilePages.push(RIGHT_PAGE);
        }
      }
      if (lastPage !== 1) {
        mobilePages.push(lastPage);
      }
      $[12] = currentPage;
      $[13] = lastPage;
      $[14] = mobilePages;
    } else {
      mobilePages = $[14];
    }
    t4 = mobilePages;
  }
  const mobilePageNumbers = t4;
  if (pageNumbers.length === 0) {
    return null;
  }
  let t5;
  if ($[15] !== backgroundColor || $[16] !== className || $[17] !== portal) {
    t5 = cx("flex items-start justify-between gap-1 py-2", backgroundColor ? backgroundColorVariant({
      backgroundColor
    }) : portal.portal === "WEB" && "bg-brown-50", className);
    $[15] = backgroundColor;
    $[16] = className;
    $[17] = portal;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const t6 = currentPage === 1;
  let t7;
  if ($[19] !== currentPage || $[20] !== handleChangePage) {
    t7 = e => {
      e.preventDefault();
      handleChangePage(currentPage > 1 ? currentPage - 1 : 1);
    };
    $[19] = currentPage;
    $[20] = handleChangePage;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== previousText) {
    t8 = <span className="hidden sm:block">{previousText}</span>;
    $[22] = previousText;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== t6 || $[25] !== t7 || $[26] !== t8) {
    t9 = <Button iconBefore="chevron-left" disabled={t6} onClick={t7} className="py-2.5! max-sm:px-2.5" variant="secondary">{t8}</Button>;
    $[24] = t6;
    $[25] = t7;
    $[26] = t8;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== backgroundColor || $[29] !== currentPage || $[30] !== handleChangePage || $[31] !== mobilePageNumbers) {
    let t11;
    if ($[33] !== backgroundColor || $[34] !== currentPage || $[35] !== handleChangePage) {
      t11 = page => {
        const isActive = currentPage === page;
        if (page === LEFT_PAGE) {
          return <span key={page} className={cx("px-4", backgroundColor === "blue-950" ? "text-white" : "text-brown-700")}>...</span>;
        }
        if (page === RIGHT_PAGE) {
          return <span key={page} className={cx("px-4", backgroundColor === "blue-950" ? "text-white" : "text-brown-700")}>...</span>;
        }
        return <button className={cx("h-11 w-11 rounded-full text-center text-base leading-none font-bold transition hover:bg-brown-200 hover:text-orange-500", isActive ? "bg-secondary text-white" : backgroundColor === "blue-950" ? textColorVariant({
          textColor: "white"
        }) : textColorVariant({
          textColor: "primary"
        }))} type="button" key={page} onClick={() => handleChangePage(page)}>{page}</button>;
      };
      $[33] = backgroundColor;
      $[34] = currentPage;
      $[35] = handleChangePage;
      $[36] = t11;
    } else {
      t11 = $[36];
    }
    t10 = mobilePageNumbers.map(t11);
    $[28] = backgroundColor;
    $[29] = currentPage;
    $[30] = handleChangePage;
    $[31] = mobilePageNumbers;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[37] !== t10) {
    t11 = <div className="block sm:hidden">{t10}</div>;
    $[37] = t10;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  let t12;
  if ($[39] !== backgroundColor || $[40] !== currentPage || $[41] !== handleChangePage || $[42] !== pageNumbers) {
    let t13;
    if ($[44] !== backgroundColor || $[45] !== currentPage || $[46] !== handleChangePage) {
      t13 = page_0 => {
        const isActive_0 = currentPage === page_0;
        if (page_0 === LEFT_PAGE) {
          return <span key={page_0} className={cx("px-4", backgroundColor === "blue-950" ? "text-white" : "text-brown-700")}>...</span>;
        }
        if (page_0 === RIGHT_PAGE) {
          return <span key={page_0} className={cx("px-4", backgroundColor === "blue-950" ? "text-white" : "text-brown-700")}>...</span>;
        }
        return <button className={cx("h-11 w-11 rounded-full text-center text-base leading-none font-bold transition hover:bg-brown-200 hover:text-orange-500", isActive_0 ? "bg-secondary text-white" : backgroundColor === "blue-950" ? textColorVariant({
          textColor: "white"
        }) : textColorVariant({
          textColor: "primary"
        }))} type="button" key={page_0} onClick={() => handleChangePage(page_0)}>{page_0}</button>;
      };
      $[44] = backgroundColor;
      $[45] = currentPage;
      $[46] = handleChangePage;
      $[47] = t13;
    } else {
      t13 = $[47];
    }
    t12 = pageNumbers.map(t13);
    $[39] = backgroundColor;
    $[40] = currentPage;
    $[41] = handleChangePage;
    $[42] = pageNumbers;
    $[43] = t12;
  } else {
    t12 = $[43];
  }
  let t13;
  if ($[48] !== t12) {
    t13 = <div className="hidden sm:block">{t12}</div>;
    $[48] = t12;
    $[49] = t13;
  } else {
    t13 = $[49];
  }
  let t14;
  if ($[50] !== t11 || $[51] !== t13) {
    t14 = <div className="flex flex-1 items-center justify-center sm:gap-2">{t11}{t13}</div>;
    $[50] = t11;
    $[51] = t13;
    $[52] = t14;
  } else {
    t14 = $[52];
  }
  const t15 = currentPage === lastPage;
  let t16;
  if ($[53] !== currentPage || $[54] !== handleChangePage) {
    t16 = e_0 => {
      e_0.preventDefault();
      handleChangePage(currentPage + 1);
    };
    $[53] = currentPage;
    $[54] = handleChangePage;
    $[55] = t16;
  } else {
    t16 = $[55];
  }
  let t17;
  if ($[56] !== nextText) {
    t17 = <span className="hidden sm:block">{nextText}</span>;
    $[56] = nextText;
    $[57] = t17;
  } else {
    t17 = $[57];
  }
  let t18;
  if ($[58] !== t15 || $[59] !== t16 || $[60] !== t17) {
    t18 = <Button iconAfter="chevron-right" size="md" disabled={t15} onClick={t16} className="py-2.5! max-sm:px-2.5" variant="secondary">{t17}</Button>;
    $[58] = t15;
    $[59] = t16;
    $[60] = t17;
    $[61] = t18;
  } else {
    t18 = $[61];
  }
  let t19;
  if ($[62] !== t14 || $[63] !== t18 || $[64] !== t5 || $[65] !== t9) {
    t19 = <menu className={t5}>{t9}{t14}{t18}</menu>;
    $[62] = t14;
    $[63] = t18;
    $[64] = t5;
    $[65] = t9;
    $[66] = t19;
  } else {
    t19 = $[66];
  }
  return t19;
}

/** @param [pageSize=10] defaults to 10 */
NumberedPagination.useState = function useNumberedPaginationState(totalItems: number, pageSize = 10) {
  const [currentNthPage, setCurrentNthPage] = useState(1);
  const totalPages = Math.floor(totalItems / pageSize);
  return {
    /** Set the current nth page */
    setCurrent: setCurrentNthPage,
    totalPages,
    /** The current nth page */
    current: currentNthPage,
    lastPage: totalPages,
    onChangePage: setCurrentNthPage,
    pageBeginIndex: (currentNthPage - 1) * pageSize,
    pageEndIndex: currentNthPage * pageSize,
    pageSize
  } as const satisfies Partial<React.ComponentProps<typeof NumberedPagination>> & Record<string, unknown>;
};